import React, { useState, useEffect, useCallback } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link } from 'react-router-dom';
import './CloudStaffDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import { FaBars, FaServer, FaUserCircle, FaSignOutAlt, FaCheck, FaTimes, FaCity } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const STATUS_CHIP = {
  pending: { bg: '#fef3c7', fg: '#b45309' },
  approved: { bg: '#d1fae5', fg: '#065f46' },
  rejected: { bg: '#fee2e2', fg: '#991b1b' },
};

const PropertyAccessRequests = () => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filter === 'all' ? '' : `?status=${filter}`;
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/property-access-requests${qs}`), { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { const d = await res.json(); setRequests(d.requests || []); }
    } catch { /* ignore */ } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const review = async (id, status) => {
    setBusyId(id);
    try {
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/property-access-requests/${id}`), {
        method: 'PATCH', headers, body: JSON.stringify({ status }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(status === 'approved' ? 'Access granted — property added to their portfolio' : 'Request rejected');
        load();
      } else {
        toast.error(data.message || 'Could not update the request');
      }
    } catch { toast.error('Network error'); } finally { setBusyId(null); }
  };

  const handleLogout = () => { clearToken(); window.location.href = '/login'; };

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <Link to="/cloud-staff/dashboard"><img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" /></Link>
          <span className="cs-header-badge">Cloud Staff</span>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <Link to="/cloud-staff/dashboard" className="cs-icon-btn" title="Dashboard"><FaServer /></Link>
            <button type="button" className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })} aria-label="User menu"><FaUserCircle /></button>
            {showUserMenu && (
              <div className="cs-user-menu">
                <button type="button" className="cs-menu-item" onClick={handleLogout}><FaSignOutAlt /> Logout</button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar active="property-access" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
        <main className="cs-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <FaCity style={{ color: '#1A80E5', fontSize: '1.4rem' }} />
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Property Access Requests</h1>
            {pendingCount > 0 && <span style={{ background: '#fef3c7', color: '#b45309', borderRadius: 999, padding: '2px 10px', fontSize: '0.78rem', fontWeight: 700 }}>{pendingCount} pending</span>}
          </div>
          <p style={{ color: '#6b7280', fontSize: '0.86rem', margin: '0 0 18px' }}>
            Contractors request to add sites to their portfolio. Approving grants their company access to the property across CloudSafe.
          </p>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {['pending', 'approved', 'rejected', 'all'].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                style={{ padding: '0.4rem 0.9rem', borderRadius: 999, border: '1px solid #e5e7eb', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                  background: filter === f ? '#1A80E5' : '#fff', color: filter === f ? '#fff' : '#374151' }}>
                {f[0].toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ color: '#9ca3af', padding: 24 }}>Loading…</div>
          ) : requests.length === 0 ? (
            <div style={{ color: '#9ca3af', padding: 24, textAlign: 'center', border: '1px dashed #e5e7eb', borderRadius: 12 }}>
              No {filter === 'all' ? '' : filter} requests.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 860 }}>
              {requests.map((r) => {
                const chip = STATUS_CHIP[r.status] || STATUS_CHIP.pending;
                return (
                  <div key={r.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 18px', background: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, fontSize: '0.98rem' }}>{r.property_name}</span>
                          {r.property_ref && <span style={{ fontSize: '0.74rem', color: '#9ca3af', fontFamily: 'monospace' }}>{r.property_ref}</span>}
                          {r.matched_property_name && <span style={{ fontSize: '0.7rem', color: '#065f46', background: '#d1fae5', borderRadius: 6, padding: '1px 7px' }}><FaCheck style={{ fontSize: '0.6rem' }} /> Matched: {r.matched_property_name}</span>}
                          {!r.matched_property_name && r.status === 'pending' && <span style={{ fontSize: '0.7rem', color: '#b45309', background: '#fef3c7', borderRadius: 6, padding: '1px 7px' }}>No exact property match</span>}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 4 }}>
                          <strong>{r.company_name || 'Contractor'}</strong>
                          {r.requested_by_name ? ` · ${r.requested_by_name}` : ''}
                          {r.created_at ? ` · ${new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                        </div>
                        {r.reason && <div style={{ fontSize: '0.82rem', color: '#374151', marginTop: 6 }}>{r.reason}</div>}
                      </div>
                      <span style={{ background: chip.bg, color: chip.fg, borderRadius: 999, padding: '2px 10px', fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {r.status[0].toUpperCase() + r.status.slice(1)}
                      </span>
                    </div>
                    {r.status === 'pending' && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button onClick={() => review(r.id, 'approved')} disabled={busyId === r.id}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '0.45rem 1rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
                          <FaCheck /> Approve & grant access
                        </button>
                        <button onClick={() => review(r.id, 'rejected')} disabled={busyId === r.id}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, padding: '0.45rem 1rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
                          <FaTimes /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default PropertyAccessRequests;
